import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { routeNeedsSession } from '../config/app';
import { Organization, OrganizationMember, Role, User } from '../types';
import { RegisterPayload, restoreSession, Session, signIn as signInService, signOut as signOutService, signUp as signUpService } from '../services/authService';
import { loadPreferences } from '../services/preferencesService';
import { ensureDefaultContent } from '../services/repair';
import { fetchIsSuperAdmin } from '../services/adminService';
import { startRealtime, stopRealtime } from '../services/realtime';
import { getWorkspaceStatus, refresh as refreshStore, subscribeWorkspaceStatus } from '../services/store';
import { can, Permission } from '../config/permissions';
import { useDatabase } from '../hooks/useDatabase';
interface AuthContextValue {
  session: Session | null;
  user: User | null;
  organization: Organization | null;
  members: OrganizationMember[];
  role: Role | null;
  loading: boolean;
  /** true mientras se recupera la sesión guardada por Supabase. */
  restoring: boolean;
  /** true si el perfil tiene `is_super_admin` activo en Supabase. */
  isSuperAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Devuelve null si la cuenta necesita confirmarse por email. */
  register: (payload: RegisterPayload) => Promise<Session | null>;
  logout: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
}
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Completa el contenido predeterminado del espacio de trabajo (formulario de
 * siete pasos y landings con y sin vídeo) en cada acceso. Es idempotente:
 * solo crea lo que falte, así que también repara organizaciones antiguas.
 */
async function repairWorkspace(session: Session): Promise<void> {
  try {
    const result = await ensureDefaultContent(session.organizationId, session.organizationName ?? 'Mi empresa');
    if (result.created || result.addedQuestions > 0) await refreshStore();
  } catch (err) {
    // No debe impedir el acceso: se registra para poder diagnosticarlo.
    console.error('[AVA] No se ha podido completar el contenido inicial:', err);
  }
}
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const db = useDatabase();
  const {
    pathname
  } = useLocation();
  // Las rutas públicas no necesitan sesión ni datos del CRM.
  const needsSession = routeNeedsSession(pathname);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(() => routeNeedsSession(window.location.pathname));
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // Evita repetir la restauración al navegar dentro de la zona privada.
  const restored = useRef(false);

  /*
   * Supabase guarda la sesión, pero restaurarla dispara `hydrate()` y con ello
   * decenas de consultas privadas (leads, notas, formularios, organización,
   * estados, fuentes…). En una landing pública, un formulario público o el 404
   * eso no debe ocurrir NUNCA, ni siquiera con una sesión abierta en otra
   * pestaña: son páginas de visitante. Por eso la restauración se hace solo
   * cuando la ruta pertenece al panel privado o al flujo de acceso, y se
   * dispara también al navegar hacia allí desde una página pública.
   */
  useEffect(() => {
    if (!needsSession) {
      // Nada que esperar: la página pública se pinta de inmediato.
      setRestoring(false);
      return;
    }
    if (restored.current) return;
    restored.current = true;
    let active = true;
    setRestoring(true);
    restoreSession().then(async next => {
      if (!active || !next) return;
      setSession(next);
      await loadPreferences(next.user.id, next.organizationId);
      await repairWorkspace(next);
      const superAdmin = await fetchIsSuperAdmin(next.user.id);
      if (active) setIsSuperAdmin(superAdmin);
    }).finally(() => {
      if (active) setRestoring(false);
    });
    return () => {
      active = false;
    };
  }, [needsSession]);
  /**
   * Sincronización en tiempo real del espacio de trabajo activo. El canal se
   * abre con la sesión, se reemplaza si cambia la organización y se cierra al
   * salir o desmontar. `startRealtime` ignora llamadas duplicadas.
   */
  useEffect(() => {
    const organizationId = session?.organizationId;
    // En una ruta pública no se abre canal alguno: el visitante no debe
    // recibir eventos del CRM de ninguna organización.
    if (!organizationId || !needsSession) {
      stopRealtime();
      return;
    }
    startRealtime(organizationId);
    return () => stopRealtime();
  }, [session?.organizationId, needsSession]);
  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const next = await signInService(email, password);
      setSession(next);
      await loadPreferences(next.user.id, next.organizationId);
      await repairWorkspace(next);
      setIsSuperAdmin(await fetchIsSuperAdmin(next.user.id));
    } finally {
      setLoading(false);
    }
  }, []);
  const register = useCallback(async (payload: RegisterPayload) => {
    setLoading(true);
    try {
      const next = await signUpService(payload);
      if (next) {
        setSession(next);
        await loadPreferences(next.user.id, next.organizationId);
      }
      return next;
    } finally {
      setLoading(false);
    }
  }, []);
  const logout = useCallback(async () => {
    stopRealtime();
    await signOutService();
    setSession(null);
    setIsSuperAdmin(false);
  }, []);
  const organization = useMemo(() => session ? db.organizations.find(o => o.id === session.organizationId) ?? null : null, [db.organizations, session]);
  const members = useMemo(() => session ? db.members.filter(m => m.organizationId === session.organizationId) : [], [db.members, session]);
  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    organization,
    members,
    role: session?.role ?? null,
    loading: loading || restoring,
    restoring,
    isSuperAdmin,
    login,
    register,
    logout,
    hasPermission: permission => session ? can(session.role, permission) : false
  };
  return <AuthContext.Provider value={value} data-id="element-861" data-mp="2av6ncwxdfhoz">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
}

/** Datos del espacio de trabajo activo, ya filtrados por organización. */
export function useWorkspace() {
  const {
    session
  } = useAuth();
  const db = useDatabase();
  const organizationId = session?.organizationId ?? '';
  // El espacio de trabajo se considera listo solo cuando el store terminó de
  // cargar ESTA organización. Las acciones de escritura deben esperar a esto:
  // con `organization_id` vacío, Supabase rechaza el insert por RLS.
  const status = useSyncExternalStore(subscribeWorkspaceStatus, getWorkspaceStatus, getWorkspaceStatus);
  const ready = Boolean(organizationId) && status.ready && status.organizationId === organizationId;
  return useMemo(() => ({
    organizationId,
    ready,
    loadingWorkspace: !ready,
    // Los elementos con borrado lógico quedan fuera de las vistas normales.
    leads: db.leads.filter(l => l.organizationId === organizationId && !l.deletedAt),
    trashedLeads: db.leads.filter(l => l.organizationId === organizationId && l.deletedAt),
    forms: db.forms.filter(f => f.organizationId === organizationId && !f.deletedAt),
    trashedForms: db.forms.filter(f => f.organizationId === organizationId && f.deletedAt),
    landings: db.landings.filter(l => l.organizationId === organizationId && !l.deletedAt),
    trashedLandings: db.landings.filter(l => l.organizationId === organizationId && l.deletedAt),
    stages: db.stages.filter(s => s.organizationId === organizationId).sort((a, b) => a.order - b.order),
    sources: db.sources.filter(s => s.organizationId === organizationId).sort((a, b) => a.order - b.order),
    notes: db.notes.filter(n => n.organizationId === organizationId && !n.deletedAt),
    history: db.history.filter(h => h.organizationId === organizationId),
    invitations: db.invitations.filter(i => i.organizationId === organizationId),
    components: db.components.filter(c => c.organizationId === organizationId),
    files: db.files.filter(f => f.organizationId === organizationId),
    isDemo: db.demo
  }), [db, organizationId, ready]);
}